import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, SaveIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Control, Controller, useForm } from "react-hook-form";
import { generatePath, useNavigate } from "react-router-dom";

import { Button, Input, PageBreadcrumb, PasswordInput, SelectV2, Tag } from "@/modules/common/components";
import {
  MANAGEMENT_USAGE_TYPE_OPTIONS,
  ManagementCompanyStatus,
  ManagementCompanyUsageType,
  ManagementModel,
} from "@/modules/common/types/ManagementCompanies.enum";
import { ROUTES } from "@/routes/constants/routes";
import useNavigateBack from "@/modules/common/hooks/useNavigateBack";
import ManagementCompanyFormFields from "../../components/ManagementCompanyFormFields";
import useCreateManagementCompany from "../../hooks/useCreateManagementCompany";
import useDemoTemplateModules from "../../hooks/useDemoTemplateModules";
import { CreateManagementCompanyFormData, ManagementCompanyBaseFormData, createManagementCompanySchema, digitsOnly } from "../../schemas";
import { resolveDemoModules } from "../../utils/demoModules";
import { useSessionStore } from "@/modules/auth/store/useSessionStore";
import { AdminUserRole } from "@/modules/common/types/Users.enum";
import CompanyCreatedModal from "./components/CompanyCreatedModal";
import ConfigureDemoModulesModal from "./components/ConfigureDemoModulesModal";

const ManagerAdd = () => {
  const { canUserSee } = useSessionStore();
  const navigate = useNavigate();
  const navigateBack = useNavigateBack(ROUTES.MANAGER.LIST);
  const createManagementCompany = useCreateManagementCompany();

  const [createdCompany, setCreatedCompany] = useState<{ id: string; ownerEmail: string; isDemonstration: boolean } | null>(null);
  const [isConfigureModulesOpen, setIsConfigureModulesOpen] = useState(false);

  const {
    register,
    control,
    watch,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateManagementCompanyFormData>({
    resolver: zodResolver(createManagementCompanySchema),
    defaultValues: {
      name: "",
      corporateName: "",
      cnpj: "",
      email: "",
      managementModel: "",
      usageType: ManagementCompanyUsageType.PRODUCTION,
      demoConfiguration: {
        modules: [],
      },
      ownerName: "",
      ownerEmail: "",
      ownerPhoneNumber: "",
      ownerPassword: "",
      ownerPasswordConfirmation: "",
    },
  });

  const usageType = watch("usageType");
  const demoModules = watch("demoConfiguration.modules") ?? [];
  const isDemoManager = usageType === ManagementCompanyUsageType.DEMONSTRATION;

  const { data: demoTemplateModules } = useDemoTemplateModules(isDemoManager);

  useEffect(() => {
    if (!isDemoManager || !demoTemplateModules) return;

    const resolvedModules = resolveDemoModules(demoModules, demoTemplateModules);
    const isMissingKeys = resolvedModules.some((key) => !demoModules.includes(key));

    if (isMissingKeys) {
      setValue("demoConfiguration.modules", resolvedModules);
    }
  }, [isDemoManager, demoTemplateModules]);

  const onSubmit = async (values: CreateManagementCompanyFormData) => {
    const data = await createManagementCompany.mutateAsync({
      name: values.name,
      corporateName: values.corporateName || undefined,
      cnpj: values.cnpj ? digitsOnly(values.cnpj) : undefined,
      email: values.email || undefined,
      managementModel: Number(values.managementModel) as ManagementModel,
      // A new gestora always starts active; a demo one has its status owned by the provisioning.
      status: isDemoManager ? undefined : ManagementCompanyStatus.ACTIVE,
      usageType: Number(values.usageType),
      demoConfiguration: isDemoManager
        ? { modules: resolveDemoModules(values.demoConfiguration?.modules ?? [], demoTemplateModules ?? []) }
        : undefined,
      owner: {
        name: values.ownerName,
        email: values.ownerEmail,
        phoneNumber: values.ownerPhoneNumber ? digitsOnly(values.ownerPhoneNumber) : undefined,
        password: isDemoManager ? values.ownerPassword : undefined,
      },
    });

    setCreatedCompany({ id: data.id, ownerEmail: data.owner.email, isDemonstration: isDemoManager });
  };

  const handleCloseSuccessModal = () => {
    if (!createdCompany) return;
    navigate(generatePath(ROUTES.MANAGER.DETAILS, { id: createdCompany.id }));
  };

  return (
    <div className="flex h-full flex-col gap-4">
      <PageBreadcrumb items={[{ label: "Gestoras", to: ROUTES.MANAGER.LIST }, { label: "Cadastrar Gestora" }]} />

      <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col">
            <p className="text-[22px] font-bold text-ui-primary">Cadastrar Gestora</p>
            <p className="text-[14px] text-ui-text-muted">Preencha os dados para cadastrar uma nova gestora.</p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              type="button"
              variant="outlined"
              label="Voltar"
              icon={<ArrowLeft />}
              className="w-full sm:w-auto"
              onClick={navigateBack}
            />
            {canUserSee(AdminUserRole.ADMIN) && (
              <Button
                type="submit"
                label="Salvar Gestora"
                icon={<SaveIcon />}
                loading={createManagementCompany.isPending}
                disabled={createManagementCompany.isPending || (isDemoManager && !demoTemplateModules)}
                className="w-full sm:w-auto"
              />
            )}
          </div>
        </div>

        <div className="flex flex-col gap-4 rounded-xl bg-white p-4">
          <div className="flex flex-col">
            <p className="text-[16px] font-bold text-ui-primary">Tipo da Gestora</p>
            <div className="flex flex-col text-[12px] text-ui-text-muted">
              <p>
                <span className="font-semibold">Produção:</span> gestora real, que começa vazia e cujo representante recebe um código de
                primeiro acesso por e-mail.
              </p>
              <p>
                <span className="font-semibold">Demonstração:</span> ambiente para apresentações, provisionado automaticamente com dados
                fictícios e com a senha de acesso definida neste cadastro.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <Controller
                name="usageType"
                control={control}
                render={({ field }) => (
                  <SelectV2
                    mode="single"
                    label="Tipo da Gestora"
                    markAsRequired
                    placeholder="Selecione tipo da gestora"
                    options={MANAGEMENT_USAGE_TYPE_OPTIONS}
                    value={String(field.value)}
                    onChange={(next) => field.onChange(next ? Number(next) : "")}
                  />
                )}
              />
              {errors.usageType && <p className="mt-1 text-xs text-red-500">{errors.usageType.message}</p>}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-4 rounded-xl bg-white p-4">
          <div className="flex flex-col">
            <p className="text-[16px] font-bold text-ui-primary">Dados Gerais</p>
            <p className="text-[12px] text-ui-text-muted">Informações cadastrais da gestora.</p>
          </div>

          <ManagementCompanyFormFields
            register={register}
            control={control as unknown as Control<ManagementCompanyBaseFormData>}
            errors={errors}
            showStatus={false}
            requireCnpj={!isDemoManager}
          />
        </div>

        <div className="flex flex-col gap-4 rounded-xl bg-white p-4">
          <div className="flex flex-wrap justify-between gap-2">
            <div className="flex flex-col">
              <p className="text-[16px] font-bold text-ui-primary">Configuração da Demonstração</p>
              <p className="text-[12px] text-ui-text-muted">
                {isDemoManager
                  ? "Selecione quais módulos e cenários deverão ser disponibilizados automaticamente nesta Gestora Demo."
                  : "Disponível apenas para gestoras de demonstração. Altere o tipo da gestora acima para configurar os módulos."}
              </p>
            </div>

            <Button
              type="button"
              label="Configurar Módulos"
              className="w-full sm:w-auto"
              disabled={!isDemoManager}
              onClick={() => setIsConfigureModulesOpen(true)}
            />
          </div>

          <Controller
            name="demoConfiguration.modules"
            control={control}
            render={({ field }) => {
              if (!isDemoManager || field.value === undefined) return <></>;

              const modulesCount = field.value.length;

              return (
                <div className="flex flex-col items-start gap-4">
                  <p className="text-ui-text-muted text-sm">{`${modulesCount} módulo(s) selecionado(s):`}</p>
                  <div className="flex flex-wrap items-center gap-2">
                    {field.value.map((value, index) => {
                      const moduleLabel = demoTemplateModules?.find((module) => module.key === value)?.name ?? "Desconhecido";
                      return <Tag key={`${value}_${index}`} label={moduleLabel} variant="primary" />;
                    })}
                  </div>
                </div>
              );
            }}
          />
        </div>

        <div className="flex flex-col gap-4 rounded-xl bg-white p-4 mb-4">
          <div className="flex flex-col">
            <p className="text-[16px] font-bold text-ui-primary">Representante Principal (Primeiro Usuário)</p>
            <p className="text-[12px] text-ui-text-muted">
              {isDemoManager
                ? "Responsável pelo acesso e gestão inicial da gestora. Ele será o primeiro usuário da gestora e entrará direto com o e-mail e a senha definidos aqui."
                : "Responsável pelo acesso e gestão inicial da gestora. Ele será o primeiro usuário da gestora e receberá um código de primeiro acesso no e-mail informado."}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <Input label="Nome" markAsRequired {...register("ownerName")} />
              {errors.ownerName && <p className="mt-1 text-xs text-red-500">{errors.ownerName.message}</p>}
            </div>

            <div>
              <Input label="E-mail" markAsRequired maskType="email" {...register("ownerEmail")} />
              {errors.ownerEmail && <p className="mt-1 text-xs text-red-500">{errors.ownerEmail.message}</p>}
            </div>

            <div>
              <Input label="Telefone" maskType="tel" {...register("ownerPhoneNumber")} />
              {errors.ownerPhoneNumber && <p className="mt-1 text-xs text-red-500">{errors.ownerPhoneNumber.message}</p>}
            </div>

            <div>
              <PasswordInput
                label="Senha de acesso"
                markAsRequired={isDemoManager}
                disabled={!isDemoManager}
                autoComplete="new-password"
                {...register("ownerPassword")}
              />
              <p className="mt-1 text-xs text-ui-text-muted">
                {isDemoManager
                  ? "Mínimo de 8 caracteres, com letra maiúscula, minúscula, número e caractere especial."
                  : "Disponível apenas para gestoras de demonstração."}
              </p>
              {errors.ownerPassword && <p className="mt-1 text-xs text-red-500">{errors.ownerPassword.message}</p>}
            </div>

            <div>
              <PasswordInput
                label="Confirmar senha de acesso"
                markAsRequired={isDemoManager}
                disabled={!isDemoManager}
                autoComplete="new-password"
                {...register("ownerPasswordConfirmation")}
              />
              {errors.ownerPasswordConfirmation && <p className="mt-1 text-xs text-red-500">{errors.ownerPasswordConfirmation.message}</p>}
            </div>
          </div>
        </div>
      </form>

      <CompanyCreatedModal
        open={!!createdCompany}
        ownerEmail={createdCompany?.ownerEmail}
        isDemonstration={createdCompany?.isDemonstration}
        onClose={handleCloseSuccessModal}
      />

      <ConfigureDemoModulesModal
        open={isConfigureModulesOpen}
        selectedModules={demoModules}
        onClose={() => setIsConfigureModulesOpen(false)}
        onSave={(modules) => setValue("demoConfiguration.modules", modules)}
      />
    </div>
  );
};

export default ManagerAdd;
